import React, { useState, useEffect } from "react";
import { apiGet } from "../../services/api";

import PatientSearch from "../doctor/PatientSearch";
import ClinicalBill from "./ClinicalBill";
import PaymentPanel from "./PaymentPanel";
import ManagerPatientHistory from "./ManagerPatientHistory";
import ReferenceInvoiceConsole from "./ReferenceInvoiceConsole";
import Button from "../common/Button";
import ChartsRangeSelector, { RangeInterval } from "../common/apexo/ChartsRangeSelector";

interface Patient {
  patient_id: string | number;
  name: string;
  gender?: string;
  age?: number;
}

interface Visit {
  visit_id: number;
  created_at?: string;
  doctor_name?: string;
  status?: string;
}

export default function ManagerBilling() {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [recentVisits, setRecentVisits] = useState<Visit[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);

  const [mode, setMode] = useState<"clinical" | "payment" | "history" | "invoice">("clinical");
  const [activeBillId, setActiveBillId] = useState<number | null>(null);

  const [rangeStart, setRangeStart] = useState(new Date());
  const [rangeEnd, setRangeEnd] = useState(new Date());
  const [interval, setInterval] = useState<RangeInterval>('month');

  useEffect(() => {
    if (!patient) {
      setRecentVisits([]);
      setSelectedVisit(null);
      return;
    }

    const patientId = encodeURIComponent(String(patient.patient_id));
    apiGet(`/api/patients/${patientId}/visits?limit=5`)
      .then((res: unknown) => {
        const list = Array.isArray(res) ? (res as Visit[]) : [];
        const sorted = [...list].sort((a, b) => b.visit_id - a.visit_id);
        setRecentVisits(sorted);
        if (sorted.length > 0) setSelectedVisit(sorted[0]);
      })
      .catch((err) => console.error("Failed to load visits", err));
  }, [patient]);

  const handleBillCreated = (res: { id: number; paid?: boolean }) => {
    setActiveBillId(res.id);
    if (!res.paid) setMode("payment");
  };

  const handlePaymentComplete = () => {
    setActiveBillId(null);
    setMode("history");
  };

  return (
    <div className="mod-layout" style={{ background: 'transparent', display: 'flex', flexDirection: 'column', height: '100%' }}>
      
      {/* Workspace Header */}
      <div className="mod-patient-card" style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
         <div style={{ flex: 1, minWidth: '300px' }}>
            <PatientSearch onSelect={setPatient} />
         </div>
         
         {patient && (
            <div className="mod-sub-nav" style={{ marginLeft: 'auto', flexWrap: 'nowrap', overflowX: 'auto' }}>
              <button type="button" className={`mod-sub-btn ${mode === "clinical" ? "active" : ""}`} onClick={() => setMode("clinical")}>Clinical Bill</button>
              <button type="button" className={`mod-sub-btn ${mode === "invoice" ? "active" : ""}`} onClick={() => setMode("invoice")}>Reference Invoice</button>
              <button type="button" className={`mod-sub-btn ${mode === "payment" ? "active" : ""}`} onClick={() => setMode("payment")}>Receive Payment</button>
              <button type="button" className={`mod-sub-btn ${mode === "history" ? "active" : ""}`} onClick={() => setMode("history")}>Patient Ledger</button>
            </div>
         )}
      </div>

      {/* Optional Apexo Range Selector if viewing history docs */}
      {patient && (mode === "history" || mode === "invoice") && (
        <div style={{ marginBottom: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center' }}>
          <ChartsRangeSelector 
            startDate={rangeStart}
            endDate={rangeEnd}
            interval={interval}
            onRangeChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
            onIntervalChange={setInterval}
          />
        </div>
      )}

      {/* Main Workspace Area */}
      <div className="mod-panel-fade-in" style={{ flex: 1, background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'auto', padding: patient ? '20px' : '0', boxShadow: 'var(--mod-shadow-sm)' }}>
        
        {!patient ? (
          <div className="mod-empty-state" style={{ height: '100%', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div className="mod-empty-icon-wrapper" style={{ marginBottom: '16px' }}>
              <span className="mod-empty-icon" style={{ opacity: 1, fontSize: '3rem' }}>💳</span>
            </div>
            <h2 style={{ fontSize: '1.4rem', color: '#0f172a', margin: '0 0 8px 0' }}>No Patient Selected</h2>
            <p style={{ color: '#64748b', fontSize: '1rem', margin: 0 }}>Search for a patient to create bills, view ledgers, or collect payments.</p>
          </div>
        ) : (
          <>
            {mode === "clinical" && (
              <ClinicalBill patient={patient} visit={selectedVisit} onCreated={(res) => handleBillCreated(res as { id: number; paid?: boolean })} />
            )}

            {mode === "payment" && (
              activeBillId ? (
                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 0' }}>
                  <h3 style={{ marginBottom: '24px', fontSize: '1.3rem', color: '#0f172a', textAlign: 'center' }}>Collect Payment for Bill #{activeBillId}</h3>
                  <PaymentPanel billId={activeBillId} onPaid={handlePaymentComplete} />
                </div>
              ) : (
                <div className="mod-empty-state" style={{ height: '400px', border: '1px dashed #cbd5e1', background: 'transparent' }}>
                  <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '20px' }}>No active bill selected for payment.</p>
                  <Button variant="outline" onClick={() => setMode("clinical")}>Create New Bill</Button>
                </div>
              )
            )}

            {mode === "invoice" && <ReferenceInvoiceConsole patient={patient} />}
            {mode === "history" && <ManagerPatientHistory patient={patient} />}
          </>
        )}
      </div>
    </div>
  );
}
